//@ts-check
import {useProductCombo} from '@/context/ProductCombo/ProductComboContext'
import {useDataTableV2} from '@/hooks/useDataTableV2'
import {getProductList} from '@/services/ProductsService/product'
import {ProductComboSteps} from '@/types/productComboTypes'
import {DEFAULT_TABLE_PAGE_SIZE} from '@/utils/constants/commonConstants'
import {getDefaultQuantity} from '@/utils/productCombo/productComboValidation'
import {keepPreviousData, useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {useCallback, useEffect, useMemo, useRef} from 'react'

/** @typedef {{column: string, direction: 'asc' | 'desc'}} SortEntry */
/** @typedef {{field: string, type: string, value?: string, values?: string[], operator?: string}} SearchFilter */

/**
 * @param {import('@/types/productComboTypes').SegmentDetails[] | undefined | null} segments
 * @returns {string[] | null}
 */
const getSegmentIds = segments => {
  const ids = segments?.flatMap(s => (s.segmentId ? [s.segmentId] : []))
  return ids?.length ? ids : null
}

/** Stable empty array so DataTable never gets a new `[]` reference */
const EMPTY_ROWS = []

const SELECT_ALL_PAGE_SIZE = 100
const SELECT_ALL_QUERY_KEY = 'PRODUCT_LIST_V2_SELECT_ALL'

const buildQueryParams = (sorting, page, pageSize) => {
  const params = new URLSearchParams()
  params.append('sortBy', sorting?.column ?? 'productId')
  params.append('order', sorting?.direction ?? 'DESC')
  params.append('page', String(page))
  params.append('pageSize', String(pageSize))
  return params.toString()
}

export const useComboProductTableV2 = (defaultPageSize = DEFAULT_TABLE_PAGE_SIZE) => {
  const {productCombo, currentStep, updateProductCombo} = useProductCombo()
  const tableRef = useRef(/** @type {any} */ (null))
  const queryClient = useQueryClient()

  const {
    pageIndex,
    pageSize,
    sorting,
    activeFilters,
    onPageIndexChange,
    onRowsPerPageChange,
    sortingHandler,
    onColumnFiltersChange,
    handleResetAll,
  } = useDataTableV2({defaultPageSize})

  const includedSegments = productCombo?.segmentConfiguration?.includedSegments?.[0]?.segments
  const excludedSegments = productCombo?.segmentConfiguration?.excludedSegment?.segments
  const isReviewStep = currentStep === ProductComboSteps.REVIEW

  // Use a ref so comboSkus never drives memo/callback deps and causes loops
  const comboSkusRef = useRef(productCombo.comboSkus)
  comboSkusRef.current = productCombo.comboSkus

  const baseFilters = /** @type {SearchFilter[]} */ ([
    {field: 'isCombo', value: 'false', type: 'string'},
    {field: 'businessGroup', value: productCombo.businessGroup, type: 'string'},
    {field: 'displayCategoryId', values: productCombo.displayCategoryIds, type: 'string'},
    {field: 'status', value: 'active', type: 'string'},
  ])

  const segmentIds = includedSegments?.length
    ? getSegmentIds(includedSegments)
    : excludedSegments?.length
      ? getSegmentIds(excludedSegments)
      : null
  const operator = includedSegments?.length ? 'include' : excludedSegments?.length ? 'exclude' : null

  if (segmentIds && operator) {
    baseFilters.push({field: 'segmentId', values: segmentIds, type: 'string', operator})
  }

  const activeSearchFilters = useMemo(
    () =>
      isReviewStep
        ? [
            ...activeFilters,
            {field: 'isCombo', value: 'false', type: 'string'},
            {field: 'productId', values: comboSkusRef.current?.map(s => s.productId), type: 'string'},
          ]
        : [...activeFilters, ...baseFilters],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeFilters, isReviewStep, productCombo.businessGroup, productCombo.displayCategoryIds, segmentIds, operator]
  )

  const isQueryEnabled = isReviewStep
    ? (productCombo.comboSkus?.length ?? 0) > 0
    : Boolean(productCombo?.businessGroup && productCombo?.displayCategoryIds?.length)

  const queryParams = buildQueryParams(sorting, pageIndex, pageSize)
  const filtersKey = useMemo(() => JSON.stringify(activeSearchFilters), [activeSearchFilters])

  const {data, isFetching} = useQuery({
    queryKey: ['PRODUCT_LIST_V2', queryParams, filtersKey, activeSearchFilters],
    queryFn: () => getProductList(activeSearchFilters, queryParams),
    enabled: isQueryEnabled,
    placeholderData: keepPreviousData,
  })

  const tableData = useMemo(
    () =>
      !isQueryEnabled ? EMPTY_ROWS : (data?.data ?? EMPTY_ROWS).map(product => ({...product, id: product.productId})),
    [data, isQueryEnabled]
  )

  const onRowSelection = useCallback(
    /** @param {any[]} selectedRows */
    selectedRows => {
      const normalize = r => r.original ?? r
      const newIds = selectedRows
        .map(r => normalize(r).productId)
        .sort()
        .join(',')
      const currentIds =
        comboSkusRef.current
          ?.map(s => s.productId)
          .sort()
          .join(',') ?? ''

      if (newIds === currentIds) return

      updateProductCombo({
        comboSkus: selectedRows.map(r => {
          const product = normalize(r)
          return {
            ...product,
            quantity: getDefaultQuantity(product.multipleSellingUnits),
          }
        }),
      })
    },
    [updateProductCombo]
  )

  const resetFilterButtonOptions = useMemo(
    () => ({onFiltersReset: handleResetAll, text: 'Reset All'}),
    [handleResetAll]
  )

  const activeSearchFiltersRef = useRef(activeSearchFilters)
  activeSearchFiltersRef.current = activeSearchFilters
  const sortingRef = useRef(sorting)
  sortingRef.current = sorting

  const {mutateAsync: fetchAllProducts, isPending: isFetchingAll} = useMutation({
    mutationKey: [SELECT_ALL_QUERY_KEY],
    mutationFn: async () => {
      const cacheKey = [SELECT_ALL_QUERY_KEY, JSON.stringify(activeSearchFiltersRef.current)]
      const cached = queryClient.getQueryData(cacheKey)
      if (cached) return cached

      const params = buildQueryParams(sortingRef.current, 0, SELECT_ALL_PAGE_SIZE)
      const response = await getProductList(activeSearchFiltersRef.current, params)
      const products = response?.data || []
      queryClient.setQueryData(cacheKey, products)
      return products
    },
  })

  const onTableReady = useCallback(
    /** @param {any} tableInstance */
    tableInstance => {
      tableRef.current = tableInstance
    },
    []
  )

  const selectingAllRef = useRef(false)
  const isSelectAllActiveRef = useRef(false)

  // When table source is disabled (e.g. no category selected), clear persisted selection.
  useEffect(() => {
    if (isQueryEnabled) return

    isSelectAllActiveRef.current = false
    tableRef.current?.setRowSelection?.({})
    onRowSelection([])
  }, [isQueryEnabled, onRowSelection])

  // When page data changes and select-all is active, restore full selection state
  useEffect(() => {
    const comboSkus = comboSkusRef.current ?? []
    if (isSelectAllActiveRef.current && tableRef.current && comboSkus.length > 0) {
      const selection = Object.fromEntries(comboSkus.map(s => [s.productId, true]))
      tableRef.current.setRowSelection(selection)
    }
  }, [tableData])

  const rowSelectionStateUpdater = useCallback(
    /** @param {any[]} selectedRows */
    async selectedRows => {
      const isSelectAll = tableRef.current?.getIsAllPageRowsSelected?.() ?? false

      if (isSelectAll) {
        if (selectingAllRef.current) return
        selectingAllRef.current = true
        isSelectAllActiveRef.current = true
        try {
          const allProducts = await fetchAllProducts()
          onRowSelection(allProducts)
        } catch {
          // selection stays as-is on error
        } finally {
          selectingAllRef.current = false
        }
        return
      }

      isSelectAllActiveRef.current = false
      onRowSelection(selectedRows)
    },
    [fetchAllProducts, onRowSelection]
  )

  const initialRowSelection = useMemo(
    () => Object.fromEntries((productCombo.comboSkus ?? []).map(s => [s.productId, true])),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [] // only set on mount — DataTable treats this as a one-time initial value
  )

  return {
    tableData,
    isFetching: isFetching || isFetchingAll,
    onPageIndexChange,
    onRowsPerPageChange,
    sortingHandler,
    onColumnFiltersChange,
    onRowSelection,
    resetFilterButtonOptions,
    onTableReady,
    rowSelectionStateUpdater,
    initialRowSelection,
    pageIndex: isFetching ? pageIndex : (data?.currentPage ?? pageIndex),
    pageSize: data?.pageSize ?? pageSize,
    totalRowsCount: isQueryEnabled ? (data?.totalRecords ?? 0) : 0,
    tableRef,
  }
}
